import { H1, H2, P, UL, LI, Code, Note, Warn, StepList } from './primitives';
import { MermaidDiagram } from './MermaidDiagram';
import { ISSUE_STATE_CHART, ISSUE_STATE_LINKS } from './architecture-charts';

export function SectionProjects() {
  return (
    <>
      <H1>Projects</H1>
      <P>
        A <strong>project</strong> links aiai·loop to a GitHub repository and
        stores per-project defaults such as the base branch.
      </P>

      <H2>Creating a project</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Open Projects',
            body: <p>Click <strong>Projects</strong> in the navigation bar.</p>,
          },
          {
            n: 2,
            title: 'Click "New project"',
            body: (
              <p>
                A modal opens. The repository list is populated from your
                GitHub token.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Select a repository',
            body: (
              <p>
                Pick the GitHub repo from the dropdown. The default branch is
                pre-filled from the repo's default.
              </p>
            ),
          },
        ]}
      />

      <H2>The project page</H2>
      <UL>
        <LI>
          All issues attached to the project, with attention badges (
          <em>Needs attention</em>, <em>Merge ready</em>).
        </LI>
        <LI>
          A <strong className="text-ink">Browse GitHub</strong> tab on the
          issues panel to pull repo issues in as runnable briefs.
        </LI>
        <LI>
          A <strong className="text-ink">Loops</strong> link to the project's
          scheduled loops (see <em>Scheduled Loops</em>).
        </LI>
        <LI>An activity summary and the count of active runs.</LI>
        <LI>
          A warning badge when no GitHub token is configured — runs would fail
          to push.
        </LI>
        <LI>
          A <strong className="text-ink">Display name</strong> card on the
          Team tab (owner-only) to rename the project. Only the name changes —
          the issue code and repo stay the same.
        </LI>
        <LI>
          Delete (removes the project record, never the GitHub repo). Its runs
          are kept, so metrics and spend history still count them.
        </LI>
      </UL>

      <H2>Transferring a project</H2>
      <P>
        A workspace owner can move a project or prototype to another
        workspace with <strong className="text-ink">Transfer</strong>. It is
        in the project page's <em>Danger zone</em> and the prototype's
        Settings <em>Danger zone</em>.
      </P>
      <UL>
        <LI>
          <strong className="text-ink">My workspaces</strong> — pick another
          workspace you own. Each one shows any problem that blocks the move,
          such as an agent still running, the repo already connected there, or
          a plan without a feature the project uses. The move happens right
          away.
        </LI>
        <LI>
          <strong className="text-ink">Another person's workspace</strong> —
          enter its slug, or the email of an owner who has exactly one
          workspace. Its owner sees an <em>Incoming transfer</em> banner on
          the Projects (or Prototypes) page and can accept or decline it.
          Nothing moves until they accept. The request expires after 7 days,
          and you can cancel it from the danger zone while it waits.
        </LI>
      </UL>
      <P>
        Everything moves with it: issues and their history, runs, loops, chat,
        the wiki and memory, and for a prototype the live app with its secrets
        and custom domains. Project access grants and pending invites reset,
        because they belong to the old workspace. A prototype follows the
        destination's workspace prototype domain; its default URL is
        unchanged.
      </P>
      <Warn>
        A prototype created through the Integration API (a factory prototype)
        is detached from it on transfer: the Integration API link and its
        webhooks stop applying.
      </Warn>

      <H2>GitHub integration</H2>
      <P>The GitHub token is used for:</P>
      <UL>
        <LI>Cloning the repo at the start of each run.</LI>
        <LI>
          <Code>git push</Code> of the feature branch and opening the pull
          request.
        </LI>
        <LI>
          The GitHub Issues backend — browsing repo issues and writing run
          results back to imported ones.
        </LI>
      </UL>
      <P>
        The token is stored AES-256-GCM encrypted in Postgres and is never
        exposed in logs or API responses — only its presence is reported.
      </P>
    </>
  );
}

export function SectionIssues() {
  return (
    <>
      <H1>Issues &amp; Briefs</H1>
      <P>
        An <strong>issue</strong> is the unit of work you hand to the agents.
        It contains a markdown brief (the task description), a chosen agent
        pipeline, and a loop limit.
      </P>

      <H2>Creating an issue</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Open the project page',
            body: (
              <p>
                Click a project card on the Projects page, then click{' '}
                <strong>New issue</strong>.
              </p>
            ),
          },
          {
            n: 2,
            title: 'Write the brief',
            body: (
              <p>
                The brief is a markdown document with a live side-by-side
                preview. Be specific: include the files to change, the desired
                behaviour, acceptance criteria, and any constraints. The richer
                the brief, the better the agents perform.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Select the pipeline',
            body: (
              <p>
                The default pipeline is <Code>Worker</Code> — a full-toolkit
                agent that reads the board, implements the change, and verifies
                it with the project's test suite. The max loops budget (5)
                bounds how many gate-failure retries the run may spend.
              </p>
            ),
          },
          {
            n: 4,
            title: 'Save or Save & run',
            body: (
              <p>
                <strong>Save</strong> parks the issue on the board in the{' '}
                <Code>open</Code> state;{' '}
                <strong>Save &amp; run</strong> saves and queues a run
                immediately, taking you to the live run page.
              </p>
            ),
          },
        ]}
      />

      <H2>Writing an effective brief</H2>
      <UL>
        <LI>
          <strong className="text-ink">Context</strong> — what the code does
          today, and why a change is needed.
        </LI>
        <LI>
          <strong className="text-ink">Goal</strong> — what you want the
          agents to produce.
        </LI>
        <LI>
          <strong className="text-ink">Acceptance criteria</strong> —
          explicit, verifiable conditions (e.g. "all existing tests must
          pass").
        </LI>
        <LI>
          <strong className="text-ink">Constraints</strong> — files to leave
          alone, APIs not to break, style guide to follow.
        </LI>
      </UL>

      <H2>Issue lifecycle</H2>
      <P>
        Every issue moves through a single <Code>state</Code> axis. The two
        orange "needs-you" states are where the agent pauses and waits for a
        human decision:
      </P>

      <MermaidDiagram
        chart={ISSUE_STATE_CHART}
        links={ISSUE_STATE_LINKS}
        ariaLabel="Issue state machine: proposed (needs approval) → open → building → review (needs merge) → closed; a failed build auto-retries back to open up to 3 attempts, then rests in failed; cancelling shelves it back to proposed."
        caption="Orange nodes require a human action; click any node to jump to its section."
      />

      <UL>
        <LI>
          <Code>proposed</Code> — filed by a loop or the PM agent; needs your
          go/no-go before anything runs. Approve it (→ <Code>open</Code>), edit
          then approve, or dismiss/close it. The{' '}
          <strong>Run</strong> button is intentionally hidden while{' '}
          <Code>proposed</Code> — approve first.
        </LI>
        <LI>
          <Code>open</Code> — approved and idle; a loop tick or the manual{' '}
          <strong>Run</strong> button starts it.
        </LI>
        <LI>
          <Code>building</Code> — a run is live right now.
        </LI>
        <LI>
          <Code>review</Code> — the build succeeded and a PR is open, waiting
          for a human to merge. The second "needs-you" gate.
        </LI>
        <LI>
          <Code>failed</Code> — the build did not land. Failed and timed-out
          builds retry automatically, up to 3 attempts per approval, carrying
          the previous error. After that the issue rests here with one "gave
          up" notification, until you approve it or press Run again. A build
          stopped by a spend cap is never auto-retried, and the issue page says
          which cap stopped it. After a per-run cost-cap stop, a workspace
          owner can press Run again and raise the cap for that one run (2×
          the cap by default, up to 10×). After a monthly-cap stop, the run
          restarts by itself once the cap resets or is raised.
        </LI>
        <LI>
          <Code>closed</Code> — terminal. A <Code>resolution</Code> records
          why: <Code>fixed</Code>, <Code>wont_do</Code>,{' '}
          <Code>duplicate</Code>, or <Code>planned</Code>.
        </LI>
      </UL>
      <P>
        On the issue detail page, a <Code>proposed</Code> issue shows an{' '}
        <strong>Approve</strong> button (moves it to <Code>open</Code>) — this
        is how you green-light proposals filed by scheduled loops. You can also{' '}
        <strong>Mark done</strong> with a note, edit, delete, or re-run.
      </P>

      <H2>Tags and sources</H2>
      <P>
        Issues carry free-form <Code>tags</Code> for grouping and filtering
        (agents commonly file <Code>bug</Code>, <Code>polish</Code>, or{' '}
        <Code>feature</Code>) and a <Code>source</Code>: created here,
        imported from GitHub, or filed by a scheduled loop.
      </P>

      <H2>Importing from GitHub Issues</H2>
      <P>
        On the project page, the issues panel's{' '}
        <strong>Browse GitHub</strong> tab lists the linked repo's issues with
        an open/closed filter. <strong>Pull in</strong> imports one — its
        title and body become the brief.
      </P>

      <H2>The issue timeline</H2>
      <P>
        The bottom of every issue page is a governance feed: created, filed by
        a loop, runs started, PR opened, auto-merge decision, human merge,
        board actions, and documents produced — the full audit trail of who
        (or what) did what, when.
      </P>
      <P>
        Issues filed by a person — the New issue form, the MCP <Code>file_issue</Code>{' '}
        tool or in-app feedback — record who filed them: the board row shows
        “by &lt;name&gt;”, the issue header says “created by …”, and the
        timeline's first entry names them (“You” when it was you). Agent, loop,
        GitHub and end-user issues keep their own provenance. In the PM chat,
        each message carries its sender and the history rail shows who started
        each conversation.
      </P>
      <P>
        The ‹ and › buttons in the issue header step to the previous and next
        issue in the board’s default order (the first 100 issues; an issue
        further down the board has them disabled).
      </P>
    </>
  );
}

export function SectionRuns() {
  return (
    <>
      <H1>Runs &amp; Live Monitoring</H1>
      <P>
        A <strong>run</strong> is one execution of an issue through the agent
        pipeline. Each run gets its own ephemeral Docker container, workspace,
        and git branch.
      </P>

      <H2>Run lifecycle</H2>
      <UL>
        <LI><Code>pending</Code> — queued, waiting for a container slot.</LI>
        <LI><Code>provisioning</Code> — container starting; repo being cloned.</LI>
        <LI><Code>running</Code> — agents executing pipeline stages.</LI>
        <LI><Code>succeeded</Code> — pipeline complete; branch pushed; PR opened.</LI>
        <LI><Code>failed</Code> — an unrecoverable error stopped the run.</LI>
        <LI><Code>cancelled</Code> — stopped by the user or stop-all.</LI>
        <LI><Code>timed_out</Code> — exceeded the configured agent timeout.</LI>
      </UL>

      <H2>The run page</H2>
      <P>
        The header shows the run status, PR state, and — while the run is
        active — a live "currently" card with the elapsed time and latest
        activity. Actions: <strong>Cancel run</strong> (active runs) and{' '}
        <strong>Refresh PR</strong> (when a PR exists). The work area has these
        tabs:
      </P>
      <UL>
        <LI>
          <strong className="text-ink">Logs</strong> — the agent timeline:
          every prose paragraph, thought, tool call, gate result, and stage
          transition, streamed live or replayed instantly for finished runs.
        </LI>
        <LI>
          <strong className="text-ink">Changes</strong> — after the run
          finishes: every changed file with a unified diff.
        </LI>
      </UL>
      <P>
        The right rail shows the <strong>Pipeline</strong> stepper (per-stage
        status, duration, cost), <strong>Details</strong> (total cost, risk
        level, branch, container, timestamps), and <strong>Token usage</strong>.
      </P>

      <H2>Alerts on the run page</H2>
      <UL>
        <LI>
          An auto-merge callout: green when the PR auto-merged or was merged
          from the app, amber with a <strong>Merge PR</strong> button when it
          is held for review — see <em>Auto-merge &amp; Risk</em>.
        </LI>
        <LI>
          A <strong>Session limit reached</strong> warning callout when the run
          hit the Claude subscription's hourly or daily usage cap, showing the
          reset time in plain language. The run fails at that point — retry it
          once the limit resets.
        </LI>
      </UL>

      <H2>Cost tracking</H2>
      <P>
        Each stage reports its cost in USD; the stepper shows per-stage cost
        and the details rail sums the total, alongside input/output/cache
        token counts.
      </P>

      <H2>Cancelling a run</H2>
      <P>
        <strong>Cancel</strong> on an active run terminates the container and
        marks the run <Code>cancelled</Code>. A branch that was already pushed
        remains on GitHub. Cancelling shelves the issue back to{' '}
        <Code>proposed</Code>: it is never retried automatically, and the PM
        only picks it up again after a new comment.
      </P>
    </>
  );
}

export function SectionLoops() {
  return (
    <>
      <H1>Scheduled Loops</H1>
      <P>
        A <strong>loop</strong> is an agent teammate on a schedule. Instead of
        you writing a brief per task, the loop clocks in on a cron expression,
        reads its standing <strong>mission</strong> plus the project's issue
        board and repository, decides what to do, and does it — then clocks
        out until the next firing.
      </P>

      <H2>What a loop can do per firing</H2>
      <UL>
        <LI>
          Fix code — implement one issue end to end and open a PR (the same
          gate rules as normal runs apply).
        </LI>
        <LI>
          File new issues onto the board (bugs, polish items, or proposals for
          you to approve).
        </LI>
        <LI>
          Manage the board — close issues, close duplicates, or send things
          back to <Code>proposed</Code> with a note.
        </LI>
        <LI>
          Nothing — an idle firing on an empty board is a successful firing.
        </LI>
      </UL>

      <H2>Creating a loop</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: "Open the project’s Loops page",
            body: (
              <p>
                From the project page, click <strong>Loops</strong>, then{' '}
                <strong>New loop</strong>.
              </p>
            ),
          },
          {
            n: 2,
            title: 'Start from a template or write your own mission',
            body: (
              <p>
                The editor opens a template gallery — pick one to pre-fill
                the name, mission prompt, and schedule. Templates include
                daily engineer, product critic, hourly bug fixer, weekly
                researcher, security audit, dependency bumper, test coverage
                raiser, dead code reaper, and docs sync. You can customise any
                template after selecting it, or write a mission from scratch.
                The mission is the loop’s personality: describe its
                priorities, rules, and what "done" means for one firing.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Pick the schedule',
            body: (
              <p>
                Choose a preset (hourly, daily, every 4 hours, …) or write a
                5-field cron expression. Times use the platform timezone.
              </p>
            ),
          },
          {
            n: 4,
            title: 'Enable it',
            body: (
              <p>
                Enabled loops fire automatically. You can also{' '}
                <strong>Fire now</strong> from the Loops page to test — a
                firing is skipped while the previous one is still running,
                and the button shows a <em>View project</em> link so you can
                navigate to the active run.
              </p>
            ),
          },
        ]}
      />

      <H2>How firings appear</H2>
      <P>
        Every firing creates an issue named after the loop and timestamp (e.g.{' '}
        <Code>Engineer — 2026-07-16 18:00</Code>) plus a run you can watch
        like any other. Issues the loop files show up on the board with
        source <em>loop</em>; proposals arrive as <Code>proposed</Code> for
        you to approve.
      </P>

      <H2>A proven pattern: the two-loop team</H2>
      <P>
        A <strong>Product Critic</strong> loop that walks the product as a
        user, reads your feedback file, and files bugs/polish/proposals — plus
        an <strong>Engineer</strong> loop that ships exactly one board issue
        per firing, test-first. The Critic finds the work, you approve the
        direction, the Engineer ships it. You stay the director; the board is
        the contract between you.
      </P>

      <Note>
        Loops run the <em>Worker</em> role (see <em>Agent Roles</em>): full
        toolkit, and a smart gate that runs the test suite only when the
        firing actually changed code — pure board-management firings don't
        need a green build to succeed.
      </Note>
    </>
  );
}

export function SectionPrototypes() {
  return (
    <>
      <H1>Prototypes</H1>
      <P>
        A <strong>prototype</strong> turns a one-paragraph idea into a working
        app with a live URL. The platform creates a private GitHub repo, an
        agent builds the first version from your description, and a preview
        container serves it on its own subdomain. From there you iterate by
        filing issues against the prototype like any project.
      </P>

      <H2>Creating one</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Prototypes → New prototype',
            body: <p>Give it a title and describe the idea.</p>,
          },
          {
            n: 2,
            title: 'Watch it build',
            body: (
              <p>
                The card shows the deploy status live:{' '}
                <Code>provisioning → building → live</Code> (or{' '}
                <Code>failed</Code> with the error on the detail page).
              </p>
            ),
          },
          {
            n: 3,
            title: 'Open the live URL',
            body: (
              <p>
                Each prototype is served at{' '}
                <Code>looproto-&lt;slug&gt;.&lt;your-domain&gt;</Code> with
                automatic TLS.
              </p>
            ),
          },
        ]}
      />

      <H2>Two flavours, detected automatically</H2>
      <UL>
        <LI>
          <strong className="text-ink">Static</strong> — a built frontend
          served by nginx.
        </LI>
        <LI>
          <strong className="text-ink">Full-stack</strong> — client + server
          in one container: a Node API with a SQLite database. The database
          lives outside the container, so{' '}
          <strong>prototype data survives redeploys</strong>.
        </LI>
      </UL>

      <H2>The prototype page</H2>
      <UL>
        <LI>
          Deploy status, live URL, and a link to the generated GitHub repo.
        </LI>
        <LI>
          <strong className="text-ink">Build output</strong> — while a deploy
          is provisioning or building, click the orange status pill to expand
          the deploy progress card, which streams the image build live (npm
          install, vite build, and deploy milestones). It starts collapsed.
          If a deploy fails,
          the last build output stays on the page under the error.
        </LI>
        <LI>
          <strong className="text-ink">Stop / Restart / Start</strong> — take
          the preview offline or recycle the container without rebuilding.
        </LI>
        <LI>
          <strong className="text-ink">Redeploy</strong> — rebuild from the
          latest main and swap the container.
        </LI>
        <LI>
          <strong className="text-ink">Open pull requests</strong> — agent PRs
          against the prototype repo, each with a Merge button.
        </LI>
        <LI>
          <strong className="text-ink">Issues</strong> — file follow-up work;
          runs behave exactly like project runs.
        </LI>
        <LI>
          <strong className="text-ink">Database</strong> and{' '}
          <strong className="text-ink">API</strong> (full-stack only) — a
          read-only diagram of the prototype's tables and a browsable list of
          its endpoints (auth-only routes carry a lock). The API description is
          read from the deployed container, not published on the public URL:
          new prototypes no longer serve <code>/api/docs</code>.
        </LI>
        <LI>
          <strong className="text-ink">Builds</strong> — every deploy
          attempt with timings, image size, and host memory headroom.
        </LI>
        <LI>
          <strong className="text-ink">Delete</strong> — removes the preview
          container <em>and</em> the GitHub repo.
        </LI>
      </UL>

      <H2>Custom domains</H2>
      <P>
        A prototype can also serve a client's own domain (e.g.{' '}
        <Code>www.client-domain.example.com</Code>) with an automatic
        certificate. Add the domain on the prototype page, have the client
        point a CNAME at the prototype's subdomain, and hit{' '}
        <strong>Verify</strong> — the platform checks DNS first, then issues
        the certificate within seconds. Domains are re-verified on every
        deploy.
      </P>

      <Warn>
        Live prototype URLs on subdomains require an instance configured with
        wildcard DNS. If prototype previews or custom domains aren't available
        on your instance, its hosting isn't set up to serve them.
      </Warn>
    </>
  );
}
